import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './StateMaster.css';

const API_BASE_URL = `${Base_Url}/api`;

const StateMaster = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [formData, setFormData] = useState({
    statename: '',
    statecode: '',
    countryid: '',
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch states from API
  const fetchStates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/state-master`);
      const result = await response.json();
      
      if (result.success) {
        setStates(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error('Failed to fetch states:', result.message);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries for dropdown
  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/country-master/active/list`);
      const result = await response.json();
      
      if (result.success) {
        setCountries(Array.isArray(result.data) ? result.data : []);
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchCountries();
  }, []);

  // Filter states based on search, status, and country
  const filteredStates = Array.isArray(states) ? states.filter(state => {
    const matchesSearch = state.statename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         state.statecode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         state.countryname.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || state.status === statusFilter;
    const matchesCountry = countryFilter === 'all' || state.countryid.toString() === countryFilter;
    return matchesSearch && matchesStatus && matchesCountry;
  }) : [];

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.statename.trim()) {
      errors.statename = 'State name is required';
    }
    
    if (!formData.statecode.trim()) {
      errors.statecode = 'State code is required';
    } else if (formData.statecode.length < 2 || formData.statecode.length > 3) {
      errors.statecode = 'State code must be 2-3 characters';
    }

    if (!formData.countryid) {
      errors.countryid = 'Country is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle add state
  const handleAddState = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/state-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowAddModal(false);
        setFormData({ statename: '', statecode: '', countryid: '', status: 'active' });
        setFormErrors({});
        fetchStates();
        showSuccess('State added successfully!');
      } else {
        showError(result.message || 'Failed to add state');
      }
    } catch (error) {
      console.error('Error adding state:', error);
      showError('Failed to add state');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit state
  const handleEditState = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/state-master/${selectedState.stateid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowEditModal(false);
        setSelectedState(null);
        setFormData({ statename: '', statecode: '', countryid: '', status: 'active' });
        setFormErrors({});
        fetchStates();
        showSuccess('State updated successfully!');
      } else {
        showError(result.message || 'Failed to update state');
      }
    } catch (error) {
      console.error('Error updating state:', error);
      showError('Failed to update state');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete state
  const handleDeleteState = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/state-master/${selectedState.stateid}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowDeleteModal(false);
        setSelectedState(null);
        fetchStates();
        showSuccess('State deleted successfully!');
      } else {
        showError(result.message || 'Failed to delete state');
      }
    } catch (error) {
      console.error('Error deleting state:', error);
      showError('Failed to delete state');
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (state) => {
    setSelectedState(state);
    setFormData({
      statename: state.statename,
      statecode: state.statecode,
      countryid: state.countryid.toString(),
      status: state.status
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (state) => {
    setSelectedState(state);
    setShowDeleteModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({ statename: '', statecode: '', countryid: '', status: 'active' });
    setFormErrors({});
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge ${status === 'active' ? 'success' : 'danger'}`}>
        {status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="state-master">
        <div className="loading">Loading states...</div>
      </div>
    );
  }

  return (
    <div className="state-master">
      {/* Header */}
      <div className="state-header">
        <div className="header-left">
          <h2>State Master</h2>
          <p>Manage states and their codes</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          <Plus size={16} />
          Add State
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search states..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <Filter size={18} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="filter-box">
          <MapPin size={18} />
          <select 
            value={countryFilter} 
            onChange={(e) => setCountryFilter(e.target.value)}
          >
            <option value="all">All Countries</option>
            {Array.isArray(countries) && countries.map(country => (
              <option key={country.countryid} value={country.countryid}>
                {country.countryname}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* States Table */}
      <div className="table-container">
        <table className="state-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>State Name</th>
              <th>State Code</th>
              <th>Country</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStates.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  <MapPin size={48} />
                  <p>No states found</p>
                </td>
              </tr>
            ) : (
              filteredStates.map(state => (
                <tr key={state.stateid}>
                  <td>{state.stateid}</td>
                  <td>{state.statename}</td>
                  <td>
                    <span className="state-code">{state.statecode}</span>
                  </td>
                  <td>
                    <div className="country-info">
                      <span className="country-name">{state.countryname}</span>
                      <span className="country-code">({state.countrycode})</span>
                    </div>
                  </td>
                  <td>{getStatusBadge(state.status)}</td>
                  <td>{new Date(state.created_at).toLocaleDateString()}</td>
                  <td>{new Date(state.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        title="Edit"
                        onClick={() => openEditModal(state)}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-icon danger" 
                        title="Delete"
                        onClick={() => openDeleteModal(state)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add State Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New State</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowAddModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddState}>
              <div className="form-group">
                <label>State Name *</label>
                <input
                  type="text"
                  name="statename"
                  value={formData.statename}
                  onChange={handleInputChange}
                  placeholder="Enter state name"
                  className={formErrors.statename ? 'error' : ''}
                />
                {formErrors.statename && (
                  <span className="error-message">{formErrors.statename}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>State Code *</label>
                <input
                  type="text"
                  name="statecode"
                  value={formData.statecode}
                  onChange={handleInputChange}
                  placeholder="e.g., MH, CA"
                  maxLength={3}
                  className={formErrors.statecode ? 'error' : ''}
                />
                {formErrors.statecode && (
                  <span className="error-message">{formErrors.statecode}</span>
                )}
              </div>

              <div className="form-group">
                <label>Country *</label>
                <select
                  name="countryid"
                  value={formData.countryid}
                  onChange={handleInputChange}
                  className={formErrors.countryid ? 'error' : ''}
                >
                  <option value="">Select Country</option>
                  {Array.isArray(countries) && countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname} ({country.countrycode})
                    </option>
                  ))}
                </select>
                {formErrors.countryid && (
                  <span className="error-message">{formErrors.countryid}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add State'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit State Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit State</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowEditModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditState}>
              <div className="form-group">
                <label>State Name *</label>
                <input
                  type="text"
                  name="statename"
                  value={formData.statename}
                  onChange={handleInputChange}
                  placeholder="Enter state name"
                  className={formErrors.statename ? 'error' : ''}
                />
                {formErrors.statename && (
                  <span className="error-message">{formErrors.statename}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>State Code *</label>
                <input
                  type="text"
                  name="statecode"
                  value={formData.statecode}
                  onChange={handleInputChange}
                  placeholder="e.g., MH, CA"
                  maxLength={3}
                  className={formErrors.statecode ? 'error' : ''}
                />
                {formErrors.statecode && (
                  <span className="error-message">{formErrors.statecode}</span>
                )}
              </div>

              <div className="form-group">
                <label>Country *</label>
                <select
                  name="countryid"
                  value={formData.countryid}
                  onChange={handleInputChange}
                  className={formErrors.countryid ? 'error' : ''}
                >
                  <option value="">Select Country</option>
                  {Array.isArray(countries) && countries.map(country => (
                    <option key={country.countryid} value={country.countryid}>
                      {country.countryname} ({country.countrycode})
                    </option>
                  ))}
                </select>
                {formErrors.countryid && (
                  <span className="error-message">{formErrors.countryid}</span>
                )}
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update State'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Delete State</h3>
              <button 
                className="btn-icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="delete-content">
              {/* AlertCircle size={48} className="warning-icon" /> */}
              <h4>Are you sure you want to delete this state?</h4>
              <p>
                <strong>{selectedState?.statename}</strong> ({selectedState?.statecode})
              </p>
              <p className="warning-text">
                This action cannot be undone. All cities associated with this state will also be deleted.
              </p>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleDeleteState}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete State'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateMaster; 